<template>
  <div>
    <div class="person-sidebar">
      <PersonFilterSidebarHeader
        ref="header"
        :searching="searching"
        :keyword="keyword"
        @search="keyword = $event"
        @open-search="openSearch"
        @close-search="closeSearch"
        @close="close"
      />

      <div
        v-if="selectedPersons.length"
        class="q-px-sm q-pt-xs"
      >
        <div class="row items-center no-wrap">
          <div class="col">
            <SelectedPersonAvatars
              :persons="selectedPersons"
              @deselect="deselect"
            />
          </div>
          <q-btn
            size="sm"
            color="grey"
            label="Clear"
            icon="fa fa-times"
            flat dense no-caps
            @click="clearSelection"
          />
        </div>
        <q-separator />
      </div>

      <div
        class="person-sidebar__scroll-wrapper"
        :style="`max-height: ${scrollHeight};`"
      >
        <PersonPicker
          ref="picker"
          :client-id="clientId"
          mode="multi"
          :model-value="selectedIds"
          :keyword="keyword"
          :empty-label="keyword ? 'No matches' : 'No people detected'"
          @update:model-value="onSelect"
          @loaded="updateKnownPersons"
        />

      </div>
    </div>
</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Person } from '@/analysis/types';
import PersonPicker from './PersonPicker.vue';
import PersonFilterSidebarHeader from './PersonFilterSidebarHeader.vue';
import SelectedPersonAvatars from './SelectedPersonAvatars.vue';

interface PersonStub {
  id: number;
  primaryFace: null;
  label: null;
}

interface Data {
  searching: boolean;
  keyword: string;
  selectedIds: number[];
  knownPersons: Record<number, Person>;
}

export default defineComponent({
  emits: ['close', 'update:value'],
  components: {
    PersonPicker,
    PersonFilterSidebarHeader,
    SelectedPersonAvatars,
  },
  props: {
    clientId: {
      type: Number,
      required: true,
    },
    value: {
      type: Array as () => number[],
      default: () => [],
    },
  },
  data(): Data {
    return {
      searching: false,
      keyword: '',
      selectedIds: [...this.value],
      knownPersons: {},
    };
  },
  computed: {
    selectedPersons(): (Person | PersonStub)[] {
      return this.selectedIds.map(id => this.knownPersons[id] || {
        id,
        primaryFace: null,
        label: null,
      });
    },
    scrollHeight(): string {
      if (this.selectedPersons.length) {
        return 'calc(100vh - 76px)';
      }
      return 'calc(100vh - 40px)';
    },
  },
  watch: {
    value(newVal: number[]) {
      this.selectedIds = [...newVal];
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },

    openSearch() {
      this.searching = true;
      this.$nextTick(() => {
        (this.$refs.header as any).focus();
      });
    },

    closeSearch() {
      this.searching = false;
      this.keyword = '';
    },

    updateKnownPersons() {
      const persons = (this.$refs.picker as any).getPersonsByIds(this.selectedIds) as Person[];
      const updated = { ...this.knownPersons };
      persons.forEach((p) => {
        updated[p.id] = p;
      });
      this.knownPersons = updated;
    },

    onSelect(ids: number[]) {
      this.selectedIds = ids;
      this.updateKnownPersons();
      this.$emit('update:value', ids);
    },

    deselect(personId: number) {
      const ids = this.selectedIds.filter(id => id !== personId);
      this.onSelect(ids);
    },

    clearSelection() {
      this.selectedIds = [];
      this.onSelect([]);
    },
  },
});
</script>

<style scoped>
.person-sidebar {
  width: 300px;
  min-height: 100%;
  background: white;
  display: flex;
  flex-direction: column;
}

.person-sidebar__scroll-wrapper {
  overflow-y: auto;
  min-height: 0;
  /* max-height: calc(100vh - 100px); */
  flex-shrink: 1;
}
</style>
